import { App, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '@stores/auth.store';
import { useStudents } from '@hooks/tenant/useStudents';
import { sendStudentAttendanceNotification } from '@services/registry';
import AttendanceGrid from './components/AttendanceGrid';

const { Title, Text } = Typography;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export default function StudentAttendance() {
    const { message } = App.useApp();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const user = useAuthStore((s) => s.user);
    const attendanceConfig = useAuthStore((s) => s.user?.attendanceConfig || null);
    const enabled = !!attendanceConfig?.configured && Array.isArray(attendanceConfig?.branches) && attendanceConfig.branches.length > 0;

    const minMonth = useMemo(() => dayjs('2026-04-01'), []);
    const now = dayjs();
    const initial = now.isBefore(minMonth, 'month') ? minMonth : now;
    const [year, setYear] = useState(initial.year());
    const [month, setMonth] = useState(initial.month() + 1); // 1-12

    const monthKey = useMemo(() => `${year}-${String(month).padStart(2, '0')}`, [year, month]);

    const { rows, loading } = useStudents({ tenantId });

    const handleStatusSaved = useCallback(
        async ({ status, dateKey, reason, row }) => {
            if (status !== 'P' && status !== 'LV') return;
            if (!tenantId || !row) return;
            await sendStudentAttendanceNotification({
                tenantId,
                student: row,
                dateKey,
                status,
                reason,
                markedBy: {
                    uid: user?.uid || null,
                    email: user?.email || null,
                    name: user?.name || null,
                },
            });
        },
        [tenantId, user?.uid, user?.email, user?.name]
    );

    const yearOptions = useMemo(() => {
        const startY = minMonth.year();
        const endY = now.year(); // no future years
        const out = [];
        for (let y = startY; y <= endY; y += 1) out.push({ value: y, label: String(y) });
        return out;
    }, [minMonth, now]);

    const monthOptions = useMemo(() => {
        const currentMonth = now.month() + 1;
        const minYear = minMonth.year();
        const minM = minMonth.month() + 1;

        const startM = year === minYear ? minM : 1;
        const endM = year === now.year() ? currentMonth : 12; // no future months

        const out = [];
        for (let m = startM; m <= endM; m += 1) {
            out.push({ value: m, label: dayjs(`${year}-${String(m).padStart(2, '0')}-01`).format('MMM') });
        }
        return out;
    }, [minMonth, now, year]);

    // clamp to [Apr 2026 .. current month]
    useEffect(() => {
        const minYear = minMonth.year();
        const minM = minMonth.month() + 1;
        const currentYear = now.year();
        const currentM = now.month() + 1;

        if (year < minYear) {
            setYear(minYear);
            setMonth(minM);
            return;
        }
        if (year > currentYear) {
            setYear(currentYear);
            setMonth(currentM);
            return;
        }
        if (year === minYear && month < minM) {
            setMonth(minM);
            return;
        }
        if (year === currentYear && month > currentM) {
            setMonth(currentM);
        }
    }, [minMonth, month, now, year]);

    if (!enabled) {
        return (
            <Shell>
                <Title level={4} style={{ margin: 0 }}>Student Attendance</Title>
                <Text type="secondary">
                    Attendance is disabled until School Time Configuration is completed.
                </Text>
            </Shell>
        );
    }

    return (
        <Shell>
            <HeaderRow>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Student Attendance</Title>
                    <Text type="secondary">Mark present/absent by day. Today defaults to Absent until you mark it.</Text>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 120 }}>
                        <Text type="secondary">Year</Text>
                        <Select
                            value={year}
                            options={yearOptions}
                            style={{ width: '100%' }}
                            onChange={(v) => setYear(v)}
                        />
                    </div>
                    <div style={{ minWidth: 140 }}>
                        <Text type="secondary">Month</Text>
                        <Select
                            value={month}
                            options={monthOptions}
                            style={{ width: '100%' }}
                            onChange={(v) => setMonth(v)}
                        />
                    </div>
                </div>
            </HeaderRow>

            <AttendanceGrid
                tenantId={tenantId}
                scope="students"
                monthKey={monthKey}
                attendanceConfig={attendanceConfig}
                rows={rows}
                loading={loading}
                getRowId={(r) => r?.id}
                getRowName={(r) => (r?.studentName || `${r?.firstName || ''} ${r?.lastName || ''}`.trim() || '—')}
                onError={(err) => message.error(err?.message || 'Attendance update failed')}
                onStatusSaved={handleStatusSaved}
            />
        </Shell>
    );
}

